import {
  type FilterStatus,
  swapsExportToCSV,
  SwapsTabView,
  type SwapData,
  type TransactionStatus,
  useInterval,
  ApproveMfaDialog,
} from '@valiant/ui-shared'
import { environment } from '../../environment'
import {
  useGetSwapsQuery,
  useApproveSwapMutation,
  useRejectSwapMutation,
} from '../../generated/graphql'
import { useState, useMemo, useRef, useCallback } from 'react'
import { useDebounce } from '@valiant/ui-shared'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../AuthProvider'

interface SwapsTabProps {
  userId?: string
  companyId?: string
  searchTerm?: string
}

export function SwapsTab({ userId, companyId, searchTerm }: SwapsTabProps) {
  const navigate = useNavigate()
  const { auth } = useAuth()
  const isGroupOwner = auth?.role === 'GROUP_OWNER'
  const [selectedStatus, setSelectedStatus] = useState<FilterStatus | null>(null)
  const [selectedFlag, setSelectedFlag] = useState<string | null>(null)

  // MFA dialog state
  const [showMfaDialog, setShowMfaDialog] = useState(false)
  const [selectedSwapId, setSelectedSwapId] = useState<string | null>(null)
  const [isApproving, setIsApproving] = useState(false)

  // Use the existing debounce hook
  const debouncedSearch = useDebounce(searchTerm || '')

  const filter = useMemo(() => {
    interface FilterObject {
      userId?: string
      companyId?: string
      search?: string
      status?: TransactionStatus
      isFlagged?: boolean
    }

    const filterObj: FilterObject = {}
    if (userId) filterObj.userId = userId
    if (companyId) filterObj.companyId = companyId
    if (debouncedSearch.trim()) filterObj.search = debouncedSearch.trim()
    if (selectedStatus) {
      //@ts-expect-error - This is a temporary fix to allow the status to be set to Awaiting
      filterObj.status = selectedStatus
    }
    if (selectedFlag) {
      filterObj.isFlagged = selectedFlag === 'FLAGGED'
    }
    return Object.keys(filterObj).length > 0 ? filterObj : undefined
  }, [debouncedSearch, selectedStatus, userId, companyId, selectedFlag])

  const [{ data: swapsData, fetching: swapsLoading, error: swapsError }, refetchSwaps] =
    useGetSwapsQuery({
      variables: { filter },
    })

  // Track whether the initial fetch has completed to avoid flickering on background refetches
  const hasLoadedRef = useRef(false)
  if (!swapsLoading) hasLoadedRef.current = true

  // Auto-refetch every 10 seconds to catch status changes, without showing loading state
  useInterval(() => {
    refetchSwaps({ requestPolicy: 'network-only' })
  }, 10000)

  // State for manual refresh loading
  const [isManualRefreshing, setIsManualRefreshing] = useState(false)

  const [, approveSwap] = useApproveSwapMutation()
  const [, rejectSwap] = useRejectSwapMutation()

  const swaps: SwapData[] = (swapsData?.getSwaps.nodes as SwapData[]) || []

  const handleApprove = (swapId: string) => {
    setSelectedSwapId(swapId)
    setShowMfaDialog(true)
  }

  const handleApproveSwap = useCallback(
    async (proof: { mfaCode?: string; passkeyApproval?: string }) => {
      if (!selectedSwapId) return

      setIsApproving(true)
      try {
        const result = await approveSwap({ input: { swapId: selectedSwapId, ...proof } })
        if (result.error) {
          throw new Error(result.error.message)
        }
        if (result.data) {
          toast.success('Swap approved successfully!')
          setShowMfaDialog(false)
          setSelectedSwapId(null)
        } else {
          toast.error('Failed to approve swap')
          throw new Error('Failed to approve swap')
        }
      } catch (error) {
        console.error('Error approving swap:', error)
        toast.error('Failed to approve swap')
      } finally {
        setIsApproving(false)
      }
    },
    [approveSwap, selectedSwapId],
  )

  const handleRejectSwap = async (swapId: string, reason: string) => {
    try {
      const result = await rejectSwap({ input: { swapId, reason } })
      if (result.data) {
        toast.success('Swap rejected successfully!')
      } else {
        toast.error('Failed to reject swap')
        throw new Error('Failed to reject swap')
      }
    } catch (error) {
      console.error('Error rejecting swap:', error)
      toast.error('Failed to reject swap')
      throw error
    }
  }

  const handleViewSwapDetails = (swapId: string) => {
    navigate(`/transactions/swap/${swapId}`)
  }

  const handleStatusChange = (status: FilterStatus | null) => {
    setSelectedStatus(status)
  }

  const handleFlagChange = (flag: string | null) => {
    setSelectedFlag(flag)
  }

  const exportToCSV = () => {
    swapsExportToCSV(swaps, environment.MSB)
  }

  const handleManualRefresh = () => {
    setIsManualRefreshing(true)
    try {
      refetchSwaps({ requestPolicy: 'network-only' })
      toast.success('Swaps refreshed successfully')
    } catch (error) {
      toast.error('Failed to refresh swaps')
      console.error('Error refreshing swaps:', error)
    } finally {
      setIsManualRefreshing(false)
    }
  }

  if (swapsError) {
    return <div>Error loading swaps: {swapsError.message}</div>
  }

  return (
    <>
      <SwapsTabView
        swaps={swaps}
        loading={swapsLoading && !hasLoadedRef.current}
        title="Swaps"
        showExportButton={true}
        showRefreshButton={true}
        showApproveButton={true}
        showRejectButton={true}
        showFlagButton={isGroupOwner}
        isComplianceUser={false}
        onApproveSwap={handleApprove}
        onRejectSwap={handleRejectSwap}
        onViewSwapDetails={handleViewSwapDetails}
        onRefresh={handleManualRefresh}
        isRefreshing={isManualRefreshing}
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        onFlagChange={handleFlagChange}
        selectedFlag={selectedFlag}
        onExportToCSV={exportToCSV}
      />

      <ApproveMfaDialog
        open={showMfaDialog}
        onOpenChange={setShowMfaDialog}
        onConfirm={handleApproveSwap}
        isSubmitting={isApproving}
        title="Approve Swap"
        apiUrl={environment.API_URL}
        token={auth?.token}
        action="approveSwap"
        targetKey={selectedSwapId ?? undefined}
      />
    </>
  )
}
